import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    Badge,
    Dialog,
    DialogContent,
    TextField,
    InputAdornment,
    Divider,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Grid,
    Checkbox,
    FormControlLabel,
    Link,
    Fade,
    Backdrop,
    Menu,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Menu as MenuIcon,
    ShoppingCart,
    KeyboardArrowDown,
    WbSunny,
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    Person,
    Phone,
    Close,
    Google,
    Facebook,
    ListAltRounded
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AuthModal from './AuthModal';
import coloredLogo from '../images/colored_logo.PNG';

const BASE_URL = 'https://dhahabjw.com';

// Utility function to handle logout
const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
};

// Cart service for API calls
const CartService = {
    async getCart() {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        try {
            const response = await fetch('https://dhahabjw.com/api/cart', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            // Check for 401 or 403 responses
            if (response.status === 401 || response.status === 403) {
                console.log('Token expired or unauthorized, logging out user');
                handleLogout();
                return null;
            }

            if (response.ok) {
                const data = await response.json();
                return data.success ? data.data : null;
            }
            return null;
        } catch (error) {
            console.error('Error fetching cart:', error);
            return null;
        }
    }
};

// Updated AppHeader Component
const AppHeader = ({ language, setLanguage, drawerOpen, setDrawerOpen, t }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const [loadingCart, setLoadingCart] = useState(false);
    const [activeTab, setActiveTab] = useState('home');

    // Store dropdown states
    const [storeMenuAnchor, setStoreMenuAnchor] = useState(null);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // Navigation items with their paths
    const navigationItems = [
        { key: 'home', label: t.home, path: '/' },
        { key: 'store', label: t.store, path: '/store' },
        { key: 'about', label: t.about, path: '/about' },
        { key: 'contact', label: t.contact, path: '/contact' }
    ];

    const handleCategoryClick = (categoryId) => {
        setStoreMenuAnchor(null);
        navigate(`/store?category=${categoryId}`); // Use navigate instead
    };

    // Also update the "All Products" click handler
    const handleAllProductsClick = () => {
        setStoreMenuAnchor(null);
        navigate('/store'); // Navigate without category parameter
    };

    // Function to determine active tab based on URL
    const getActiveTabFromUrl = (url = window.location.pathname) => {
        const path = url.replace(/\/$/, '') || '/';
        const pathToKey = {
            '/': 'home',
            '/store': 'store',
            '/calculator': 'priceCalculator',
            '/about': 'about',
            '/contact': 'contact'
        };
        return pathToKey[path] || 'home';
    };

    // Fetch categories for dropdown
    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/api/categories`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            // Check for 401 or 403 responses
            if (response.status === 401 || response.status === 403) {
                console.log('Token expired or unauthorized while fetching categories, logging out user');
                handleLogout();
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setCategories(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Helper function for localized category names
    const getLocalizedCategoryName = (category) => {
        if (typeof category.name === 'object' && category.name !== null) {
            return category.name[language] || category.name.en || '';
        }
        return category.name || '';
    };

    // Handle store menu operations
    const handleStoreMenuOpen = (event) => {
        setStoreMenuAnchor(event.currentTarget);
    };

    const handleStoreMenuClose = () => {
        setStoreMenuAnchor(null);
    };

    // Set initial active tab based on current URL
    useEffect(() => {
        setActiveTab(getActiveTabFromUrl());
    }, []);

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, []);

    // Check authentication and load cart
    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('token');
            const userData = localStorage.getItem('user');
            if (token && userData) {
                setUser(JSON.parse(userData));
                await loadCartCount();
            }
        };
        initializeAuth();
    }, []);

    // ADD THIS: Listen for cart updates from other components
    useEffect(() => {
        const handleCartUpdate = async (event) => {
            console.log('Cart update event received:', event.detail);
            // Refresh cart count when cart is updated
            await loadCartCount();
        };

        // Listen for custom cart update events
        window.addEventListener('cartUpdated', handleCartUpdate);

        // Also listen for storage changes (in case of multiple tabs)
        const handleStorageChange = (e) => {
            if (e.key === 'cartUpdated') {
                loadCartCount();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('cartUpdated', handleCartUpdate);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const handleNavigation = (item) => {
        setActiveTab(item.key);
        sessionStorage.setItem('selectedTab', item.key);
        window.location.href = item.path;
        setTimeout(() => {
            setActiveTab(getActiveTabFromUrl());
        }, 300);
    };

    // Listen for storage events to sync active tab across tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'selectedTab' && e.newValue) {
                const currentPath = getActiveTabFromUrl();
                if (e.newValue === currentPath) {
                    setActiveTab(e.newValue);
                }
            }
        };

        const storedTab = sessionStorage.getItem('selectedTab');
        const currentTab = getActiveTabFromUrl();
        if (storedTab === currentTab) {
            setActiveTab(storedTab);
        }

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const loadCartCount = async () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (!token || !user) {
            setCartCount(0);
            return;
        }

        setLoadingCart(true);
        try {
            const cartData = await CartService.getCart();
            if (cartData) {
                setCartCount(cartData.itemCount || 0);
                console.log('Cart count updated:', cartData.itemCount || 0);
            } else {
                setCartCount(0);
            }
        } catch (error) {
            console.error('Error loading cart count:', error);
            setCartCount(0);
        } finally {
            setLoadingCart(false);
        }
    };

    const handleCartClick = () => {
        if (!user) {
            setAuthModalOpen(true);
        } else {
            window.location.href = '/cart';
        }
    };

    const handleOrderClick = () => {
        if (!user) {
            setAuthModalOpen(true);
        } else {
            window.location.href = '/orders';
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setCartCount(0);
        window.location.reload();
    };

    const handleLoginSuccess = () => {
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    };

    return (
        <AppBar position="static" sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
            <Toolbar
                sx={{
                    px: { xs: 2, sm: 3, md: 4 }, // Add horizontal padding
                }}
            >
                {/* Left side - Menu button */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            mr: { xs: 1, sm: 2 },
                            display: { lg: 'none' },
                            p: { xs: 1, sm: 1.5 }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: 1
                }}>
                    {/* Left side - Desktop Navigation */}
                    <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1 }}>
                        {navigationItems.map((item) => (
                            <Box key={item.key} sx={{ position: 'relative' }}>
                                {item.key === 'store' ? (
                                    // Store item with dropdown
                                    <>
                                        <Button
                                            color="inherit"
                                            onClick={handleStoreMenuOpen}
                                            sx={{
                                                position: 'relative',
                                                color: activeTab === item.key ? 'primary.main' : 'inherit',
                                                fontWeight: activeTab === item.key ? 600 : 400,
                                                transition: 'all 0.3s ease',
                                                px: { lg: 1.5, xl: 2 },
                                                fontSize: { lg: '0.875rem', xl: '1rem' },
                                                '&:hover': {
                                                    color: 'primary.main',
                                                    backgroundColor: 'rgba(212, 175, 55, 0.1)'
                                                },
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: activeTab === item.key ? '80%' : '0%',
                                                    height: '2px',
                                                    backgroundColor: 'primary.main',
                                                    transition: 'width 0.3s ease'
                                                }
                                            }}
                                            endIcon={<KeyboardArrowDown />}
                                        >
                                            {item.label}
                                        </Button>

                                        {/* Store Categories Dropdown Menu */}
                                        <Menu
                                            anchorEl={storeMenuAnchor}
                                            open={Boolean(storeMenuAnchor)}
                                            onClose={handleStoreMenuClose}
                                            MenuListProps={{
                                                'aria-labelledby': 'store-button',
                                            }}
                                            sx={{
                                                mt: 1,
                                                '& .MuiPaper-root': {
                                                    minWidth: 200,
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            {/* All Products Option */}
                                            <MenuItem onClick={() => handleNavigation(item)}>
                                                <Typography>{t.allProducts || 'All Products'}</Typography>
                                            </MenuItem>

                                            <Divider />

                                            {/* Category Options */}
                                            {categoriesLoading ? (
                                                <MenuItem disabled>
                                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                                    <Typography>{t.loading || 'Loading...'}</Typography>
                                                </MenuItem>
                                            ) : (
                                                categories.map((category) => (
                                                    <MenuItem
                                                        key={category._id}
                                                        onClick={() => handleCategoryClick(category._id)}
                                                    >
                                                        <Typography>{getLocalizedCategoryName(category)}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Menu>
                                    </>
                                ) : (
                                    // Regular navigation items
                                    <Button
                                        color="inherit"
                                        onClick={() => handleNavigation(item)}
                                        sx={{
                                            position: 'relative',
                                            color: activeTab === item.key ? 'primary.main' : 'inherit',
                                            fontWeight: activeTab === item.key ? 600 : 400,
                                            transition: 'all 0.3s ease',
                                            px: { lg: 1.5, xl: 2 },
                                            fontSize: { lg: '0.875rem', xl: '1rem' },
                                            '&:hover': {
                                                color: 'primary.main',
                                                backgroundColor: 'rgba(212, 175, 55, 0.1)'
                                            },
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: activeTab === item.key ? '80%' : '0%',
                                                height: '2px',
                                                backgroundColor: 'primary.main',
                                                transition: 'width 0.3s ease'
                                            }
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* Center - Logo Image */}
                    <Box
                        sx={{
                            flexGrow: { xs: 1, lg: 0 },
                            display: 'flex',
                            justifyContent: { xs: 'center', lg: 'flex-start' },
                            mr: { lg: 4 },
                            cursor: 'pointer',
                        }}
                        onClick={() => handleNavigation({ key: 'home', path: '/' })}
                    >
                        <img
                            src={coloredLogo}
                            alt="Logo"
                            style={{
                                height: '100px', // Adjust height as needed
                                width: 'auto',
                                maxWidth: '150px', // Maximum width to prevent it from being too large
                                objectFit: 'contain', // Maintain aspect ratio
                            }}
                        />
                    </Box>

                    {/* Right side -  Language, Auth, Cart */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl>
                            <Select
                                value={language}
                                onChange={(e) => {
                                    localStorage.setItem('lang', e.target.value);
                                    setLanguage(e.target.value)
                                }}

                                sx={{
                                    minWidth: { xs: 60, sm: 80 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    '& .MuiSelect-select': {
                                        py: { xs: 0.5, sm: 1 },
                                        px: { xs: 1, sm: 1.5 }
                                    }
                                }}
                            >
                                <MenuItem value="en">EN</MenuItem>
                                <MenuItem value="ar">العربية</MenuItem>
                            </Select>
                        </FormControl>

                        {/* User Auth Section */}
                        {user ? (
  <>
    <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', md: 'inline' } }}>
      {t.welcome}, {user.profile ? user.profile.firstName || user.profile.name : ''}
    </Typography>
    
    {/* Profile Button */}
    <Button
      variant="outlined"
      size="small"
      onClick={() => navigate('/profile')}
      sx={{
        mr: 1,
        borderColor: 'primary.main',
        color: 'primary.main',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        px: { xs: 1, sm: 1.5 },
        display: { xs: 'none', sm: 'inline-flex' }
      }}
    >
      <Person sx={{ mr: 0.5, fontSize: '1rem' }} />
      {t.profile || 'Profile'}
    </Button>

    <Button
      variant="outlined"
      color="error"
      size="small"
      onClick={handleLogout}
      sx={{
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        px: { xs: 1, sm: 1.5 }
      }}
    >
      {/* Show short text on mobile */}
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
        {t.logoutShort}
      </Box>
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {t.logout}
      </Box>
    </Button>
  </>
) : (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setAuthModalOpen(true)}
                                sx={{
                                    ml: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    px: { xs: 1, sm: 1.5 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    minWidth: { xs: 'auto', sm: '64px' }
                                }}
                            >
                                {/* Show short text on mobile */}
                                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                    {t.login}
                                </Box>
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                    {t.login}
                                </Box>
                            </Button>
                        )}

                        {/* Cart Icon */}
                        <IconButton color="inherit" onClick={handleCartClick}>
                            <Badge badgeContent={cartCount} color="primary">
                                <ShoppingCart />
                            </Badge>
                        </IconButton>
                        {loadingCart && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}

                        {/* Orders Icon */}
                        {!isMobile && (
                            <IconButton color="inherit" onClick={handleOrderClick}>
                                <ListAltRounded />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </Toolbar>

            {/* Auth Modal */}
            <AuthModal
                open={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                language={language}
                t={t}
                onLoginSuccess={handleLoginSuccess}
            />
        </AppBar>
    );
};

export default AppHeader;